import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@aviary";
import { l } from "@shared/locales/i18n";
import type { PatientFilter } from "@shared/types/PatientSearch";

import type { Filter } from "../CatalogSearch";

import { ActiveTags } from "./ActiveTags/ActiveTags";
import { ShowAllTagButton } from "./ShowAllTagButton/ShowAllTagButton";

interface Props {
  filterTags: Filter[] | PatientFilter[];
  onClearAllFilterTags: () => void;
  onRemoveFilterTag: (filter: Filter | PatientFilter) => void;
  maxFilterTagsToShow?: number;
}

const FilterTags = ({
  filterTags,
  onClearAllFilterTags,
  onRemoveFilterTag,
  maxFilterTagsToShow = 3,
}: Props) => {
  const [showAllFilterTags, setShowAllFilterTags] = useState(false);

  const { t } = useTranslation();

  const showMoreLessAriaLabel = showAllFilterTags
    ? t(l.sharedCommon.ShowLess)
    : t(l.sharedCommon.ShowMore);

  const renderShowAllTagButton = () => {
    if (filterTags.length > maxFilterTagsToShow) {
      return (
        <ShowAllTagButton
          aria-label={showMoreLessAriaLabel}
          handleClick={() => setShowAllFilterTags(!showAllFilterTags)}
          isShowingAll={showAllFilterTags}
          remainder={filterTags.slice(maxFilterTagsToShow, filterTags.length).length}
        />
      );
    }
  };

  const renderClearAllFilterTagsButton = () =>
    onClearAllFilterTags && (
      <Button
        aria-label={t(l.sharedCommon.ClearAllSearchFilters)}
        isText
        isColor="info"
        onClick={onClearAllFilterTags}
      >
        {t(l.sharedCommon.ClearFilters)}
      </Button>
    );

  const filterTagsToDisplay = showAllFilterTags
    ? filterTags
    : filterTags.slice(0, maxFilterTagsToShow);

  return (
    <>
      <ActiveTags tags={filterTagsToDisplay} handleOnRemove={onRemoveFilterTag} />
      {renderShowAllTagButton()}
      {renderClearAllFilterTagsButton()}
    </>
  );
};

export { FilterTags };
